/**
 * listDeleteImpact — what deleting a message list does to the campaigns that
 * use it. Shared by the Mass and Priority list managers: the backend removes
 * the list from every campaign's distribution (and pauses a campaign left
 * with nothing to send), so the operator is told up front and afterwards.
 */
import type { TFunction } from 'i18next';
import type { ListDeleteResult } from '../../api/client';

export interface CampaignListRef {
  id: number;
  name: string;
  is_active: boolean;
  lists_distribution: Record<string, number> | null;
}

export interface ListDeleteImpact {
  /** Campaigns whose distribution includes the list. */
  affected: CampaignListRef[];
  /** Active campaigns for which this is the only list — they will be paused. */
  willPause: CampaignListRef[];
}

export function campaignsUsingList(campaigns: CampaignListRef[], listId: number): ListDeleteImpact {
  const key = String(listId);
  const affected = campaigns.filter((c) => Object.prototype.hasOwnProperty.call(c.lists_distribution || {}, key));
  const willPause = affected.filter((c) => c.is_active && Object.keys(c.lists_distribution || {}).length === 1);
  return { affected, willPause };
}

const names = (cs: { name: string }[]) => cs.map((c) => `“${c.name}”`).join(', ');

/** Confirm-dialog text: the plain question plus the campaign consequences, if any. */
export function deleteListConfirmText(t: TFunction, impact: ListDeleteImpact, question: string): string {
  const lines = [question];
  if (impact.affected.length) {
    lines.push('', t('listsManager.deleteImpactUsedBy', { count: impact.affected.length, campaigns: names(impact.affected) }));
  }
  if (impact.willPause.length) {
    lines.push(t('listsManager.deleteImpactWillPause', { count: impact.willPause.length, campaigns: names(impact.willPause) }));
  }
  return lines.join('\n');
}

/** Post-delete notice when the backend had to pause campaigns; null when nothing to say. */
export function deleteListOutcomeText(t: TFunction, result: ListDeleteResult | undefined): string | null {
  const paused = result?.campaigns_deactivated ?? [];
  if (!paused.length) return null;
  return t('listsManager.deletePausedCampaigns', { count: paused.length, campaigns: names(paused) });
}
